import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"
import { useContext } from "react"
import { PlanLimitBanner, type PlanLimitBannerProps } from "@/components/onboarding/PlanLimitBanner"
import { AuthContext } from "@/hooks/use-auth"
import type { AvailableTenant } from "@/hooks/use-auth"
import { useToast } from "@/hooks/use-toast"
import { closeHostedPlanLimit, hostPlanLimit, openModalDialog } from "@/lib/plan-limit-host"
import { extractErrorBodyField, extractErrorStatus, problemDetail, translateError } from "@/lib/error-messages"
import { organisationNameOf, planRequestMailto } from "@/lib/plan-offers"
import { planLabel } from "@/lib/pricing-page"
import { asUpgradeTargetPlan, recordFunnelStep, recordUpgradeRequested } from "@/services/funnel"

/** Statut HTTP d'un refus de limite de plan. */
const PAYMENT_REQUIRED = 402

/** Refus de limite de plan, prêt à afficher. */
type PlanLimitNotice = {
  /** « Limite du plan Gratuit atteinte ». */
  title: string
  /** Message du serveur, traduit. */
  detail: string
  /** Plan proposé par le serveur, nommé comme la page Tarifs. */
  upgradeLabel: string
  /** Code du plan proposé, pour le funnel d'upgrade. */
  upgradePlan: string
  /** Porte de sortie : « Continuer en Gratuit » en plan Gratuit, sinon « Plus tard ». */
  laterLabel: string
  /** Nom et identifiant de l'organisme, pour la demande de passage. */
  organisationName: string
  organisationSlug: string
}

/**
 * Lit un refus de limite de plan (402).
 * Plan proposé et message viennent du serveur.
 * Autre erreur : `O.none`.
 */
function usePlanLimitNotice(): (error: unknown) => O.Option<PlanLimitNotice> {
  // Hors connexion (tests, pages publiques) : plan Gratuit par défaut.
  const auth = O.fromNullable(useContext(AuthContext))
  return (error) => {
    if (extractErrorStatus(error) !== PAYMENT_REQUIRED) return O.none
    const upgradePlan = pipe(
      extractErrorBodyField(error, "upgrade_plan"),
      O.filter((plan): plan is string => typeof plan === "string"),
      O.getOrElse(() => "equipe"),
    )
    const upgradeLabel = planLabel(upgradePlan)
    const user = pipe(
      auth,
      O.chain((session) => O.fromNullable(session.user)),
    )
    // Le plan du refus prime sur `user.plan`.
    // Celui-ci date du dernier chargement de la session.
    const currentPlan = pipe(
      extractErrorBodyField(error, "current_plan"),
      O.filter((plan): plan is string => typeof plan === "string"),
      O.alt<string>(() =>
        pipe(
          user,
          O.map((current): string => current.plan),
        ),
      ),
      O.getOrElse(() => "free"),
    )
    const organisationSlug = pipe(
      user,
      O.map((current) => current.tenant_code),
      O.getOrElse(() => ""),
    )
    const tenants = pipe(
      auth,
      O.map((session) => session.availableTenants),
      O.getOrElse((): AvailableTenant[] => []),
    )
    const currentLabel = planLabel(currentPlan)
    return O.some({
      title: `Limite du plan ${currentLabel} atteinte`,
      // Le serveur écrit déjà ce message en français.
      // Jamais enveloppé en erreur générique.
      detail: pipe(
        problemDetail(error),
        O.getOrElse(() => translateError(error)),
      ),
      upgradeLabel,
      upgradePlan,
      laterLabel: currentPlan === "free" ? "Continuer en Gratuit" : "Plus tard",
      organisationName: organisationNameOf(tenants, organisationSlug),
      organisationSlug,
    })
  }
}

/**
 * Props de `PlanLimitBanner` pour `notice`, étapes du funnel comprises.
 * L'affichage est enregistré à l'appel.
 * Le clic ouvre la demande de passage par e-mail.
 * La porte de sortie appelle `close`.
 * Un chemin unique pour le toast et la fenêtre modale.
 * Seule la porte de sortie enregistre `upgrade_prompt_dismissed`.
 * Échap, la croix ou un autre toast ne répondent pas.
 */
function presentPlanLimit(notice: PlanLimitNotice, close: () => void): PlanLimitBannerProps {
  const target_plan = asUpgradeTargetPlan(notice.upgradePlan)
  const recordStep = (event: "upgrade_prompt_shown" | "upgrade_prompt_clicked" | "upgrade_prompt_dismissed") => {
    if (target_plan) recordFunnelStep({ event, context: "plan_limit", target_plan })
  }
  recordStep("upgrade_prompt_shown")
  return {
    detail: notice.detail,
    upgradeLabel: notice.upgradeLabel,
    laterLabel: notice.laterLabel,
    onUpgrade: () => {
      recordStep("upgrade_prompt_clicked")
      // La demande de passage est l'e-mail lui-même : il n'y a pas de paiement en ligne.
      recordUpgradeRequested(notice.upgradePlan)
      window.location.href = planRequestMailto(notice.upgradeLabel, notice.organisationName, notice.organisationSlug)
    },
    onLater: () => {
      recordStep("upgrade_prompt_dismissed")
      close()
    },
  }
}

/**
 * Écran d'un refus de limite de plan (`PlanLimitBanner`).
 * Il remplace le toast d'erreur générique.
 * Non bloquant, il reste en coin d'écran, sans minuterie.
 * Il attend la réponse de l'utilisateur.
 * Sous une fenêtre modale, il s'affiche dans la fenêtre (`PlanLimitDialogHost`).
 * Le clavier ne sort pas d'une modale.
 * L'écran part avec la fenêtre.
 * Ne la fermez pas après un refus.
 * Le bouton d'upgrade ouvre un e-mail de demande.
 * La porte de sortie, de même poids, le ferme.
 * Une limite atteinte s'affiche à chaque refus.
 * Ce n'est pas un encart proactif.
 * Autre erreur : rien d'affiché, rend `false`.
 *
 * @example
 * const showPlanLimit = usePlanLimitToast()
 * showPlanLimit(error) // true : « Limite du plan Gratuit atteinte »
 */
export function usePlanLimitToast(): (error: unknown) => boolean {
  const readPlanLimit = usePlanLimitNotice()
  const { toast } = useToast()
  return (error) =>
    pipe(
      readPlanLimit(error),
      O.match(
        () => false,
        (notice) => {
          const modal = openModalDialog()
          if (O.isSome(modal)) {
            hostPlanLimit({
              container: modal.value,
              title: notice.title,
              banner: presentPlanLimit(notice, closeHostedPlanLimit),
            })
            return true
          }
          const shown = toast({
            title: notice.title,
            duration: Infinity,
            description: <PlanLimitBanner {...presentPlanLimit(notice, () => shown.dismiss())} />,
          })
          return true
        },
      ),
    )
}
